import { selectActiveMediaDevices } from 'features/user-media/userMediaSlice';
import { store } from 'store/store';
import { MediaStreamType } from 'features/streaming/types';
import { stopStreamTracks } from 'features/user-media/utils';
import { compoundStream } from 'features/user-media/utils/compoundStream';
import { BasePublishing } from 'utils/webrtc/publishing';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { logger } from 'utils/logger';
import { isValidVBSelection } from 'utils/types';
import { selectVirtualBackgroundSyncConfig } from 'features/virtual-backgrounds/selectors';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { PublishingOptions } from 'utils/webrtc/types';
import { defaultSimulcastMaxBitrates } from 'utils/webrtc/environment';
import { getDefaultVideoConstraints } from 'features/user-media/utils/getDefaultVideoConstraints';
import { getDefaultAudioConstraints } from 'features/user-media/utils/getDefaultAudioConstraints';
import { selectHDVideoSettings } from 'features/streaming/streamingSlice';
import Janus from 'lib/janus';

interface JoinMedia {
  audio: MediaStream | null;
  video: MediaStream | null;
}

export class PublishingFeed extends BasePublishing {
  joinMedia: JoinMedia = {
    audio: null,
    video: null,
  };

  constructor() {
    super('publishing');
  }

  getActiveMediaConfig = () => {
    const activeMediaDevices = selectActiveMediaDevices(store.getState());

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    return {
      video: videoEnabled
        ? {
            ...getDefaultVideoConstraints(),
            deviceId: {
              exact: activeMediaDevices.videoinput,
            },
          }
        : undefined,
      audio: audioEnabled
        ? {
            ...getDefaultAudioConstraints(),
            deviceId: {
              exact: activeMediaDevices.audioinput,
            },
          }
        : undefined,
    };
  };

  protected getSimulcastMaxBitrates = () => {
    const hdVideoSettings = selectHDVideoSettings(store.getState());

    return {
      ...defaultSimulcastMaxBitrates,
      high: hdVideoSettings.bitrate,
    };
  };

  protected getSendEncodings = () => {
    const hdVideoSettings = selectHDVideoSettings(store.getState());
    const bitrates = this.getSimulcastMaxBitrates();
    const encodings = {
      h: { rid: 'h', active: hdVideoSettings.enabled, maxBitrate: bitrates.high },
      m: {
        rid: 'm',
        active: true,
        maxBitrate: bitrates.medium,
        scaleResolutionDownBy: 2,
      },
      l: {
        rid: 'l',
        active: true,
        maxBitrate: bitrates.low,
        scaleResolutionDownBy: 4,
      },
    };

    return Object.values(encodings);
  };

  protected isSimulcastEnabled = () => Janus.webRTCAdapter.browserDetails.browser !== 'firefox';

  setJoinMedia = (stream: MediaStream | null, kind: MediaStreamType) => {
    this.joinMedia[kind] = stream;
  };

  cleanupJoinMedia = (kind?: MediaStreamType) => {
    if (!kind) {
      Object.keys(this.joinMedia).forEach((key) => {
        const media = key as MediaStreamType;

        stopStreamTracks(this.joinMedia[media]);
        this.joinMedia[media] = null;
      });

      return;
    }

    stopStreamTracks(this.joinMedia[kind]);
    this.joinMedia[kind] = null;
  };

  getJoinPublishingOptions = async (): Promise<PublishingOptions> => {
    let stream = compoundStream(this.joinMedia.audio, this.joinMedia.video);
    if (!stream) {
      return {
        video: this.mediaStates.video.enabled,
        audio: this.mediaStates.audio.enabled,
      };
    }

    // This handles scenarios where the join screen is disabled and
    // the virtual background (VB) is configured via the SDK,
    // making it impossible to apply the VB on the join screen
    const syncVBConfig = selectVirtualBackgroundSyncConfig(store.getState());
    if (isValidVBSelection(syncVBConfig) && !VirtualBackgroundManager.activated) {
      stream = await this.applyVirtualBackground(stream, syncVBConfig);
    }

    return {
      video: !!stream.getVideoTracks().length,
      audio: !!stream.getAudioTracks().length,
      stream,
    };
  };

  getResetMediaStates = (preserve: boolean) => {
    if (this.joinMedia?.audio || this.joinMedia?.video) {
      return {
        preserveVideo: !!this.joinMedia.video,
        preserveAudio: !!this.joinMedia.audio,
      };
    }

    if (preserve) {
      return {
        preserveVideo: this.mediaStates.video.enabled,
        preserveAudio: this.mediaStates.audio.broadcasting, // cant use enabled since its always on, because of how we handle audio
      };
    }

    return {
      preserveVideo: this.defaultMediaState.enabled,
      preserveAudio: this.defaultMediaState.enabled,
    };
  };

  private async applyVirtualBackground(
    stream: MediaStream,
    config: SelectedVirtualBackground
  ): Promise<MediaStream> {
    if (stream.getVideoTracks().length > 0) {
      try {
        const VBStream = await this.activateVirtualBackground(config, {
          stream,
        });
        if (VBStream) {
          return VBStream;
        }
      } catch (error) {
        logger.remote().debug('Error applying virtual background:', error);
      }
    }
    return stream;
  }
}
